import { useCallback, useEffect, useState } from "react";
import { ApiError } from "../../shared/api/client";
import { recordPayment } from "./api";
import type { RecordPaymentRequest } from "./types";

/**
 * Payments offline support (flow-09 S1 offline variant).
 * A recorded payment is queued locally when the device is offline and
 * replayed on the next `online` event with its ORIGINAL Idempotency-Key —
 * so a replay after an ambiguous timeout can never double-apply (server
 * catches duplicates by reference + signature as well).
 *
 * Mirrors the flow-02/flow-07 localStorage pattern: small JSON payloads,
 * best-effort, upgrade path is IndexedDB.
 */

const QUEUE_KEY = "pg_pay_queue_v1";

export type QueuedPaymentStatus = "pending-sync" | "synced" | "failed";

export interface QueuedPaymentRecord {
  /** Device-local id ("pay-<uuid>"). */
  localId: string;
  payload: RecordPaymentRequest;
  /** Generated once at record time and reused on every replay. */
  idempotencyKey: string;
  status: QueuedPaymentStatus;
  createdAt: string;
  /** Display hydration for the queued state. */
  customerName: string;
  /** Set after a successful sync (undefined when the intake was invalid —
      invalid payments have no id/number by design; see exceptionNumber). */
  paymentId?: number;
  paymentNumber?: string;
  /** REC-YYYY-#### when the synced intake was invalid → reconciliation. */
  exceptionNumber?: string;
  /** Set when the server rejected the record (4xx) — not retried blindly. */
  failReason?: string;
}

function readQueue(): QueuedPaymentRecord[] {
  try {
    const raw = localStorage.getItem(QUEUE_KEY);
    return raw ? (JSON.parse(raw) as QueuedPaymentRecord[]) : [];
  } catch {
    return [];
  }
}

function writeQueue(records: QueuedPaymentRecord[]): void {
  try {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(records));
  } catch {
    /* best-effort */
  }
}

export function listQueuedPayments(): QueuedPaymentRecord[] {
  return readQueue();
}

export function getQueuedPayment(localId: string): QueuedPaymentRecord | undefined {
  return readQueue().find((r) => r.localId === localId);
}

export function enqueuePayment(record: QueuedPaymentRecord): void {
  writeQueue([record, ...readQueue()]);
}

function updateQueuedPayment(localId: string, patch: Partial<QueuedPaymentRecord>): void {
  writeQueue(readQueue().map((r) => (r.localId === localId ? { ...r, ...patch } : r)));
}

export function queuedPaymentCount(): number {
  return readQueue().filter((r) => r.status === "pending-sync").length;
}

/** Replay pending records in FIFO order; failures stay queued for next time. */
export async function syncPaymentQueue(): Promise<void> {
  const pending = readQueue()
    .filter((r) => r.status === "pending-sync")
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  for (const record of pending) {
    try {
      const res = await recordPayment(record.payload, record.idempotencyKey);
      updateQueuedPayment(record.localId, {
        status: "synced",
        paymentId: res.payment.id ?? undefined,
        paymentNumber: res.payment.payment_number ?? undefined,
        exceptionNumber: res.payment.exception_number ?? undefined,
      });
    } catch (err) {
      if (err instanceof ApiError && err.status >= 400 && err.status < 500) {
        // Definitive rejection (validation etc.) — surface it, stop retrying.
        updateQueuedPayment(record.localId, { status: "failed", failReason: err.message });
      }
      /* network errors: keep pending-sync for the next online event */
    }
  }
}

/** Hook: live online status + queue replay on reconnect + pending count. */
export function usePaymentSync() {
  const [online, setOnline] = useState<boolean>(() => navigator.onLine);
  const [pending, setPending] = useState<number>(() => queuedPaymentCount());
  const [syncing, setSyncing] = useState(false);

  const refresh = useCallback(() => setPending(queuedPaymentCount()), []);

  const sync = useCallback(async () => {
    if (!navigator.onLine || queuedPaymentCount() === 0) {
      refresh();
      return;
    }
    setSyncing(true);
    try {
      await syncPaymentQueue();
    } finally {
      setSyncing(false);
      refresh();
    }
  }, [refresh]);

  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    window.addEventListener("focus", refresh);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
      window.removeEventListener("focus", refresh);
    };
  }, [refresh]);

  useEffect(() => {
    if (online) void sync();
  }, [online, sync]);

  return { online, pending, syncing, sync, refresh };
}
